import { useLanguage, useMessageActionContext } from '@/app/providers';
import { Checkbox, CustomButton } from '@/components';
import ChatProgressLoader from '@/features/chats/ui/components/Progress/ChatProgressLoader';
import MessageActionDropdown from '@/features/chats/ui/Conversation/components/MessageActionDropdown';
import { useMessageAction } from '@/features/chats/viewmodels/useMessageAction';
import { useMessageFormatter } from '@/features/chats/viewmodels/useMessageFormatter';
import { useMessageTruncation } from '@/features/chats/viewmodels/useMessageTruncation';
import { formatChatTime } from '@/features/timeformatter/timeStamp';
import { Username, UserProfile } from '@/features/users';
import { getLocalUserId, getMessageSenderId } from '@/shared/utils';
import { isGroupChat } from '@/features/chats/utils/jidIdFormat';
import { getThumbBase64URL, getMediaDimensions } from '@/shared/utils/mediaUtils';
import PropTypes from 'prop-types';
import { memo, useMemo, useState } from 'react';
import MessageStatus from '../MessageStatus';
import ReplyMessageCard from '../ReplyMessageCard';
import { StarIcon } from '@/assets/images';

const SENDER_CLASS = 'sender-chat ct-rowReverse chat-bubble ct-flex ct-aiEnd ct-gap10 ct-padding-lr8-rl16';
const RECEIVER_CLASS = 'receiver-chat chat-bubble ct-flex ct-gap10 ct-padding-lr8-rl16';
const SENDER_INNER = 'ct-flex ct-flexColEnd ct-relative ct-gap10';
const RECEIVER_INNER = 'ct-flex ct-flexCol ct-relative ct-gap10';

const ImageBox = memo(({ message, onMediaClick, activeChatJid, showProfile, senderId }) => {
   const { t } = useLanguage();
   const [isFullImageLoaded, setIsFullImageLoaded] = useState(false);

   const { caption, blobUrl, base64Thumb, mentionedUsersIds, thumbStyle, width, height } = useMemo(() => {
      const { media = {}, mentionedUsersIds = [] } = message.msgBody ?? {};
      const { caption = '', blobUrl = '', thumb_image = '', webWidth, webHeight } = media;
      const base64Thumb = getThumbBase64URL(thumb_image);
      const { width: displayWidth, height: displayHeight } = getMediaDimensions(webWidth, webHeight);

      return {
         caption: caption.trim(),
         blobUrl,
         base64Thumb,
         mentionedUsersIds,
         width: displayWidth,
         height: displayHeight,
         thumbStyle: base64Thumb
            ? {
                 backgroundImage: `url(${base64Thumb})`,
                 backgroundSize: 'cover',
                 backgroundPosition: 'center',
                 backgroundRepeat: 'no-repeat',
              }
            : undefined,
      };
   }, [message.msgBody]);

   const { isTruncated, isExpanded, displayText, toggleExpand } = useMessageTruncation(caption);
   const formattedCaption = useMessageFormatter(displayText, { mentionedUsersIds, isClickable: true });
   const hasCaption = caption && caption.trim().length > 0;

   return (
      <div
         className={`chat-box chat-img-box ct-flex ct-flexCol ${hasCaption ? 'ct-gap10' : 'no-caption'} ${blobUrl || base64Thumb ? 'videoimagebubble' : ''} ${message.msgBody?.replyTo ? 'reply-bubble' : ''}`}
         style={{ width: `${width / 16}rem` }}
      >
         {showProfile && <Username userId={senderId} />}
         <ReplyMessageCard replyTo={message.msgBody?.replyTo} activeChatJid={activeChatJid} className="reply" />
         <div className="document-img ct-flex ct-fcol ct-aiStart ct-gap8 ct-relative">
            <ChatProgressLoader message={message} />
            <button
               className="imageFile"
               onClick={onMediaClick}
               style={{ ...thumbStyle, width: `${width / 16}rem`, height: `${height / 16}rem` }}
            >
               {blobUrl ? (
                  <img
                     className="imageFile"
                     src={blobUrl}
                     style={{ opacity: isFullImageLoaded ? 1 : 0 }}
                     onLoad={() => setIsFullImageLoaded(true)}
                     alt="Full sized"
                  />
               ) : (
                  base64Thumb && <img className="imageFile" src={base64Thumb} alt="Thumbnail" />
               )}
            </button>
         </div>
         {hasCaption && (
            <p>
               {formattedCaption}
               <br />
               {isTruncated && (
                  <button className="view-more-less-btn" onClick={toggleExpand}>
                     {isExpanded ? t('message.VIEW_LESS') : t('message.VIEW_MORE')}
                  </button>
               )}
            </p>
         )}
      </div>
   );
});

ImageBox.displayName = 'ImageBox';
ImageBox.propTypes = {
   message: PropTypes.object.isRequired,
   onMediaClick: PropTypes.func,
   activeChatJid: PropTypes.string,
   showProfile: PropTypes.bool,
   senderId: PropTypes.string,
};

const ImageCard = memo(({ message, activeChatJid }) => {
   const { t } = useLanguage();
   const { hideAvatar = false, hideActions = false, disableMediaClick = false } = useMessageActionContext();
   const { isSelected, isSelectionActive, handleToggleSelection, handleMediaClick } = useMessageAction(
      message,
      activeChatJid,
   );
   const senderId = getMessageSenderId({ type: 'chats', message });
   const isSender = senderId === getLocalUserId();

   const timestamp = useMemo(
      () => (message.timestamp ? formatChatTime(new Date(Number(message.timestamp)), 'message-card') : ''),
      [message.timestamp],
   );
   const hideAvatarImage = isSender || hideAvatar;
   const isChatGroup = useMemo(() => isGroupChat(activeChatJid), [activeChatJid]);
   const showProfile = isChatGroup && !hideAvatarImage;

   const isHighlighted = message?.isHighlighted;

   return (
      <CustomButton
         as="div"
         className={`message-row custom-image-card ${isSelectionActive && !hideActions ? 'cts-image ct-flex ct-aiCenter selection-active' : ''} ${isSelected && !hideActions ? 'cts-image selected' : ''} ${isHighlighted && !hideActions ? 'cts-image search-highlight-active' : ''}`}
         onClick={isSelectionActive && !hideActions ? handleToggleSelection : null}
      >
         {isSelectionActive && !hideActions && (
            <div className="selection-checkbox-wrap cts-image">
               <Checkbox checked={isSelected} onChange={handleToggleSelection} />
            </div>
         )}
         <div className={`${isSender ? SENDER_CLASS : RECEIVER_CLASS}`}>
            {showProfile && (
               <picture className="chat-avatar cts-image">
                  <UserProfile userId={senderId} className="chat-avatar chat-avatar-fallback ct-flexCenter" />
               </picture>
            )}
            <div
               className={`${isSender ? SENDER_INNER : RECEIVER_INNER} ${message.favouriteStatus === 1 ? 'starredMessage' : ''}`}
            >
               <ImageBox
                  message={message}
                  onMediaClick={disableMediaClick ? undefined : handleMediaClick}
                  activeChatJid={activeChatJid}
                  showProfile={showProfile}
                  senderId={senderId}
               />
               <button className={`ct-flexAlign ct-flex ct-gap8 ${isSender ? 'ct-rowReverse' : ''}`}>
                  {isSender && <MessageStatus msgStatus={message.msgStatus} />}
                  <span className="time cts-image">{timestamp}</span>
                  {message.editedStatus === 1 && <span className="edited-icon">{t('message.EDITED')}</span>}
                  {message.favouriteStatus === 1 && (
                     <i className="icon starIcon ct-flexCenter">
                        <StarIcon />
                     </i>
                  )}
               </button>
               {!hideActions && (
                  <MessageActionDropdown
                     className="cts-image"
                     message={message}
                     isSender={isSender}
                     activeChatJid={activeChatJid}
                  />
               )}
            </div>
         </div>
      </CustomButton>
   );
});

ImageCard.displayName = 'ImageCard';
ImageCard.propTypes = {
   message: PropTypes.object.isRequired,
   activeChatJid: PropTypes.string,
};

export default ImageCard;
